import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';

import { LazyLoadImageModule } from 'ng-lazyload-image';

import { WalletAddressDirective } from '@/directives/wallet-address.directive';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { CamelCase2TitleCase } from '@/pipes/cc2tc.pipe';
import { UsdPipe } from '@/pipes/usd.pipe';

import { DataService } from '@/services/data.service';
import { PlatformService } from '@/services/platform.service';

import { BehaviorSubject, Subject, catchError, forkJoin, map, of, switchMap, takeUntil, tap, timeout, withLatestFrom } from 'rxjs';

import { Apollo, gql } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client';

import { Event } from '@/models/graph';

import { ZERO_ADDRESS } from '@/constants/utils';
import { ENSMetadata } from '@/models/ens';
import { Web3Service } from '@/services/web3.service';
import { environment } from 'src/environments/environment';
import { StateService } from '@/services/state.service';
import { TippyDirective } from '@/directives/tippy.directive';
import { LoaderComponent } from '../icons/loader.component';


@Component({
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    RouterModule,
    LazyLoadImageModule,
    WalletAddressDirective,
    WeiToEthPipe,
    TippyDirective,
    FormatCashPipe,
    LoaderComponent,
    CamelCase2TitleCase,
    UsdPipe
  ],
  selector: 'app-ens-metadata',
  templateUrl: './ens-metadata.html',
  styleUrls: ['./ens-metadata.scss']
})
export class EnsMetadataComponent implements OnDestroy, OnChanges {
  ZERO_ADDRESS = ZERO_ADDRESS;

  @Input() address: string|undefined;

  private destroy$ = new Subject<void>();

  private ensMetadata = new BehaviorSubject<ENSMetadata | null>(null);
  ensMetadata$ = this.ensMetadata.asObservable();
  ensMetadataLoading:boolean = false
  hasDescription:boolean = false
  hasENS:boolean = false
  hasMetadatas:boolean = false
  filteredMeta: { label: string; value: string | number | null; }[] = [];

  @ViewChild('details') details:ElementRef|undefined;

  environment = environment

  constructor(
    private apollo: Apollo,
    public stateSvc: StateService,
    public web3Svc: Web3Service,
    public host: ElementRef,
    public dataSvc: DataService,
    private platformService: PlatformService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.address && changes.address.currentValue !== changes.address.previousValue) {
      this.load();
    }
  
  }

  ngOnInit() {
    this.ensMetadata.subscribe(metadata => {
      if (!metadata) {
        this.filteredMeta = []
      } else {
        this.filteredMeta = [
          { label: 'ens', value: metadata.ens },
          { label: 'x', value: metadata.x },
          { label: 'discord', value: metadata.discord },
          { label: 'telegram', value: metadata.telegram },
          { label: 'github', value: metadata.github },
          { label: 'url', value: metadata.url },
          { label: 'followingCount', value: metadata.followingCount },
          { label: 'followerCount', value: metadata.followerCount },
        ].filter(item => !!item.value || item.label === 'followerCount' || item.label === 'followingCount');
        
        this.hasDescription = !!(metadata.description && metadata.description != '')
        this.hasENS = !!(metadata.ens && metadata.ens != '')
        this.hasMetadatas = this.filteredMeta.length > 3
        this.ensMetadataLoading = false
      }

      this.platformService.setTimeout(() => {
        if (!this.details) return
        
        if (this.details!.nativeElement.clientHeight + 10 >= this.details!.nativeElement.scrollHeight) {
          this.details!.nativeElement.classList.remove('expandable')
        } else {
          this.close()
        }
      }, 0)
    })

    this.ensMetadataLoading = true
  }

  load() {

    console.log('loading ens metadata', this.address);
    this.ensMetadata.next(null);
    this.ensMetadataLoading = true;
  
    const ens$ = this.web3Svc.getENSMetadata(this.address!);
  
    const enriched$ = ens$.pipe(
      switchMap(ens =>
        forkJoin({
          stats: this.dataSvc.getEthFollowStatsFor(this.address!).pipe(
            timeout(5000),
            catchError(() => of(null)) // ignore timeout or error
          ),
          ens: of(ens)
        }).pipe(
          map(({ stats, ens }) => {
            console.log('ens metadata loaded', ens);
            if (stats) {
              return {
                ...ens!,
                followerCount: stats?.followers_count ?? 0,
                followingCount: stats?.following_count ?? 0
              };
              //return ens;
            }
            return ens;
          })
        )
      )
    );
  
    enriched$.subscribe(enrichedEns => {
      this.ensMetadataLoading = false;
      this.ensMetadata.next(enrichedEns);
    });  
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle() {
    this.details!.nativeElement.classList.toggle('open')
    if (this.details!.nativeElement.classList.contains('open')) {
      this.details!.nativeElement.style.height = this.details!.nativeElement.scrollHeight + 30 + 'px';
    } else {
      this.close()
    }
  }

  close() {
    let h = 3
    if (this.hasENS) h += 3.5
    if (this.hasDescription) h += 2.5
    if (!this.hasMetadatas) h += 5.7
    this.details!.nativeElement.style.height = `${h}rem`    
  }
}
